import { useRef, useState } from 'preact/hooks';

/** Ширина полосы у левого края, с которой начинается жест возврата. */
export const EDGE_ZONE = 28;
/** Сколько нужно протянуть, чтобы возврат сработал. */
const THRESHOLD = 80;
/** Дальше экран не едет. */
const MAX_SHIFT = 140;

/**
 * Свайп от левого края вправо — возврат назад.
 *
 * Нужен прежде всего на iOS: в приложении, добавленном на домашний экран,
 * системного жеста «назад» нет вовсе, и уйти из чата можно только кнопкой
 * в углу. На Android системный жест есть и работает через историю браузера,
 * но лишним этот путь не будет — он одинаков на обеих системах.
 */
export function useEdgeSwipeBack(onBack: () => void) {
  const [shift, setShift] = useState(0);
  const start = useRef<{ x: number; y: number } | null>(null);
  const engaged = useRef(false);

  /**
   * Сдвиг дублируется в ref, потому что состояние обновляется не сразу.
   * Обработчик завершения жеста, читая только состояние, видел бы значение
   * до последнего движения пальца — и жест не срабатывал бы.
   */
  const current = useRef(0);
  const move = (value: number) => {
    current.current = value;
    setShift(value);
  };

  const onTouchStart = (event: TouchEvent) => {
    const touch = event.touches[0];
    if (!touch) return;

    // Жест начинается только у самого края. Иначе он перехватывал бы
    // свайп цитирования на сообщениях.
    if (touch.clientX > EDGE_ZONE) {
      engaged.current = false;
      return;
    }
    start.current = { x: touch.clientX, y: touch.clientY };
    engaged.current = true;
  };

  const onTouchMove = (event: TouchEvent) => {
    const touch = event.touches[0];
    if (!touch || !engaged.current || !start.current) return;

    const dx = touch.clientX - start.current.x;
    const dy = touch.clientY - start.current.y;

    // Палец пошёл вертикально — это прокрутка, а не возврат.
    if (Math.abs(dy) > Math.abs(dx) && Math.abs(dy) > 12) {
      engaged.current = false;
      move(0);
      return;
    }
    if (dx <= 0) {
      move(0);
      return;
    }
    move(Math.min(MAX_SHIFT, dx));
  };

  const onTouchEnd = () => {
    if (engaged.current && current.current >= THRESHOLD) onBack();
    move(0);
    engaged.current = false;
    start.current = null;
  };

  return {
    shift,
    handlers: { onTouchStart, onTouchMove, onTouchEnd, onTouchCancel: onTouchEnd },
  };
}
